// Per-record change-history modal for the designer live preview.
//
// Fetches one record's audit timeline from /api/livepreview/history and
// renders it with the shared <AuditTimeline> (same component the standalone
// runtime uses), so the preview and the deployed app show an identical view.

import { useEffect, useState } from 'react'
import { AuditTimeline, type AuditEntry } from '../../AuditTimeline'
import { parseApiError } from '../utils'

/** Fetches one record's audit timeline. Injected by the hosting app (the
 *  platform pre-binds its `livePreviewRecordHistory`; the standalone runtime
 *  supplies its own) so the package carries no HTTP client. Mirrors the
 *  platform fetcher's signature/return exactly. */
export type RecordHistoryFetcher = (
  project: string, objName: string, txnId: string,
  limit?: number, offset?: number,
) => Promise<{ ok?: boolean; entries: AuditEntry[] }>

interface Props {
  project: string
  objName: string
  txnId: string
  onClose: () => void
  fetchHistory: RecordHistoryFetcher
}

export default function RecordHistoryModal({ project, objName, txnId, onClose, fetchHistory }: Props) {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setError(null)
    fetchHistory(project, objName, txnId)
      .then((r) => { if (!cancelled) setEntries(r.entries || []) })
      .catch((e) => { if (!cancelled) setError(parseApiError(e).message) })
    return () => { cancelled = true }
  }, [project, objName, txnId, fetchHistory])

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
          borderRadius: 12, padding: 20, minWidth: 400, maxWidth: 580,
          maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Record history</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
        {error && <div style={{ color: 'var(--color-error, #dc2626)', fontSize: 14 }}>{error}</div>}
        {!error && entries === null && (
          <div style={{ color: 'var(--color-text-secondary)', fontSize: 14, padding: '12px 0' }}>Loading…</div>
        )}
        {!error && entries !== null && <AuditTimeline entries={entries} />}
      </div>
    </div>
  )
}
